export type PillarKey = "pr" | "content-and-seo" | "digital-marketing";

export const pillarMeta: Record<
  PillarKey,
  { title: string; subtitle: string; blurb: string; chipClass: string }
> = {
  "pr": {
    title: "PR & Communications",
    subtitle: "Clarify your story. Earn credibility. Lead the market.",
    blurb:
      "ICP, message map, exec voice & ghostwriting, PR, and thought-leadership programs.",
    chipClass: "pill-position",
  },
  "content-and-seo": {
    title: "Content & SEO",
    subtitle: "Build the engine. Grow demand.",
    blurb:
      "Topic maps, briefs, production ops, and analytics stitched to revenue signals.",
    chipClass: "pill-visibility",
  },
  "digital-marketing": {
    title: "Digital Marketing",
    subtitle: "Offline → online, elegantly.",
    blurb:
      "Websites, UX content, Google Business, and foundational funnels that move.",
    chipClass: "pill-digital",
  },
};

/** Client engagement under an agency (no logo used). */
export type ClientEngagement = {
  slug: string;
  name: string;
  region: string;
  years: string;
  teaser: string;
  work: string[];
};

export type Company = {
  slug: string;
  name: string;
  pillar: PillarKey;
  pillarLabel: string;
  region: string;
  years: string;
  teaser: string;
  work: string[];
  logo: string; // filename under /public/logos
  assets?: string[];
  meta?: string[];
  related?: { slug: string; name: string }[];
  /** When set, this entry is an agency and lists client engagements (no client logos). */
  clients?: ClientEngagement[];
};

export const companies: Company[] = [
  // ---------- Visibility ----------
  {
    slug: "espncricinfo",
    name: "ESPNcricinfo",
    pillar: "content-and-seo",
    pillarLabel: "Content & SEO",
    region: "Global",
    years: "2016–22, 2023–present",
    teaser: "Two stints: newsroom rigor → Shorts growth engine.",
    work: [
      "Phase 2 (2023–): Guardrails, repeatable formats, and on-call scripts for Shorts growth",
      "Phase 1 (2016–22): Features, live commentary, editorial ops/workflows",
    ],
    logo: "espncricinfo.png",
    related: [
      { slug: "dream11", name: "Dream11" },
      { slug: "porter", name: "Porter" },
      { slug: "preseasoned", name: "Preseasoned (Altletics)" },
    ],
  },
  {
    slug: "dream11",
    name: "Dream11",
    pillar: "content-and-seo",
    pillarLabel: "Content & SEO",
    region: "India",
    years: "2022",
    teaser: "High-volume SEO articles via Altletics.",
    work: ["~30 SEO blogs/month mapped to priority keywords & intent"],
    logo: "dream11.jpg",
    related: [{ slug: "fancode", name: "FanCode" }],
  },
  {
    slug: "fancode",
    name: "FanCode",
    pillar: "content-and-seo",
    pillarLabel: "Content & SEO",
    region: "India",
    years: "2022",
    teaser: "Recurring SEO production via Altletics.",
    work: ["Editorial pipeline and briefs for ongoing SEO blog output"],
    logo: "fancode.png",
  },
  {
    slug: "catena-media",
    name: "Catena Media",
    pillar: "content-and-seo",
    pillarLabel: "Content & SEO",
    region: "Global",
    years: "2022",
    teaser: "Performance SEO content at scale (via Altletics).",
    work: ["High-velocity SEO articles aligned to affiliate/performance goals"],
    logo: "catena.png",
  },
  {
    slug: "isl",
    name: "ISL",
    pillar: "content-and-seo",
    pillarLabel: "Content & SEO",
    region: "India",
    years: "2022",
    teaser: "SEO content for sports-betting ecosystems (via Altletics).",
    work: ["Recurring SEO production with strict topical maps and guidelines"],
    logo: "isl.png",
  },
  {
    slug: "porter",
    name: "Porter",
    pillar: "content-and-seo",
    pillarLabel: "Content & SEO",
    region: "India",
    years: "2021",
    teaser: "Early-stage SEO blog series for logistics startup.",
    work: ["Research, briefs, and articles to seed organic visibility"],
    logo: "porter.png",
  },
  {
    slug: "preseasoned",
    name: "Preseasoned (Altletics)",
    pillar: "content-and-seo",
    pillarLabel: "Content & SEO",
    region: "India",
    years: "2022 (6-month project)",
    teaser: "0→1 GTM & SEO without paid spend, inside Altletics.",
    work: [
      "Topic map, content ops, and on-site SEO for early traction",
      "Lightweight GTM plan and measurement cadence",
    ],
    logo: "preseasoned.webp",
  },

  // ---------- PR & Communications (agency + standalone) ----------
  {
    slug: "your-wordsmiths",
    name: "Your Wordsmiths",
    pillar: "pr",
    pillarLabel: "PR & Communications",
    region: "Dubai, MENA",
    years: "2022–present",
    teaser: "Executive writing for multiple business leaders in the UAE via the agency.",
    work: [],
    logo: "your-wordsmiths.png",
    clients: [
      { slug: "continental", name: "Continental Group", region: "MENA", years: "2022–present", teaser: "Executive ghostwriting & PR across the Middle East.", work: ["Op-eds, interviews, press releases", "Leadership voice & narrative bank"] },
      { slug: "webengage", name: "WebEngage", region: "MENA", years: "2022–present", teaser: "Thought leadership and PR/editorials for SaaS leadership.", work: ["Press releases, interviews, and op-eds for regional markets"] },
      { slug: "dake-rechsand", name: "Dake Rechsand", region: "MENA", years: "2022–present", teaser: "Sustainability PR across the Middle East.", work: ["Releases, features, and technical editorial support"] },
      { slug: "masafi", name: "Masafi", region: "MENA", years: "2023–present", teaser: "Editorials & PR for a leading FMCG brand.", work: ["Narratives, op-eds, and multi-market releases"] },
      { slug: "amantra", name: "Amantra", region: "MENA", years: "2023–present", teaser: "Thought leadership for AI-led facilities management.", work: ["Exec ghostwriting, platform narrative, editorial roadmap"] },
      { slug: "gupshup", name: "Gupshup", region: "MENA", years: "2023–present", teaser: "Executive narratives for conversational AI.", work: ["Bylines and op-eds for leadership across the region"] },
      { slug: "innovation-crew", name: "Innovation Crew", region: "MENA", years: "2022–present", teaser: "PR/editorials for a BTL agency.", work: ["Press office cadence and leadership pieces"] },
      { slug: "khimji-family", name: "Khimji Family", region: "MENA", years: "2023–present", teaser: "Private-client ghostwriting and positioning.", work: ["Executive profiles, op-eds, narrative framing"] },
      { slug: "maan-igp", name: "Ma'an-IGP", region: "MENA", years: "2023–present", teaser: "Thought leadership for a legacy planning startup.", work: ["Founder ghostwriting and editorial ops"] },
      { slug: "optimum-nutrition", name: "Optimum Nutrition", region: "MENA", years: "2023–present", teaser: "PR and thought leadership for sports nutrition brand.", work: ["Press releases, executive narratives, and regional market positioning"] },
    ],
  },
  {
    slug: "hexon-global",
    name: "Hexon Global",
    pillar: "pr",
    pillarLabel: "PR & Communications",
    region: "MENA",
    years: "2022–23",
    teaser: "LinkedIn presence and positioning (via Spacekayak).",
    work: ["Exec voice, cadence, topical map for B2B manufacturing"],
    logo: "hexon.png",
  },
  {
    slug: "fancraze",
    name: "FanCraze",
    pillar: "pr",
    pillarLabel: "PR & Communications",
    region: "Global",
    years: "2022–23",
    teaser: "UI/UX content lead for app redesign (via Spacekayak).",
    work: ["Information architecture of flows", "Microcopy and UX voice & tone"],
    logo: "fancraze.jpg",
  },

  // ---------- Digital ----------
  {
    slug: "hegde-dental",
    name: "Hegde Dental Clinic",
    pillar: "digital-marketing",
    pillarLabel: "Digital Marketing",
    region: "India",
    years: "2023",
    teaser: "Website + Google Business from zero to live.",
    work: ["Website build, GBP setup, baseline funnel for calls/visits"],
    logo: "hegde-dental.png",
  },
  {
    slug: "talaga-by-mir",
    name: "Talaga by Mir",
    pillar: "digital-marketing",
    pillarLabel: "Digital Marketing",
    region: "India",
    years: "2023",
    teaser: "Brought a heritage boutique online.",
    work: ["Site build, catalog copy, Google Business presence"],
    logo: "talaga.jpg",
  },
];

/** Slugs of clients under Your Wordsmiths (for legacy /companies/:slug redirects). */
export const yourWordsmithsClientSlugs = new Set(
  (companies.find((c) => c.slug === "your-wordsmiths")?.clients ?? []).map((cl) => cl.slug)
);